import { Coordinates, Currency } from '../core/api/general/general.types';
import { OrderState } from '../core/api/order/order.types';
import { PaymentAccountType } from '../core/api/payment/payment.types';
import {
  ConcessionStore,
  DeliveryType,
  PickupLocationType,
  ServiceChargeConfig,
  TableServiceCategory,
} from '../core/api/store/store.types';

export interface Ordered {
  DisplayOrder: number;
}

interface Common extends Ordered {
  Name?: string;
  Price?: number;
  ImageUrl?: string;
  CellLayoutType: CellLayoutType | Fd.MenuSectionItemBase['CellLayoutType'];
}

export interface CuisineType {
  CuisineTypeId: number;
  Name: string;
}

export interface Language {
  LangageId: string;
  Name: string;
}

export enum GatewayPaymentAccount {
  Ideal = 'Ideal',
  Sofort = 'Sofort',
  Giropay = 'Giropay',
  Eps = 'Eps',
  Alipay = 'Alipay',
  Multibanco = 'Multibanco',
  Bancontact = 'Bancontact',
  P24 = 'P24',
}

export enum WebToAppRedirect {
  NoRedirect = 0,
  RedirectOnce = 1,
  RedirectAlways = 2,
  SuggestPwa = 3,
  ForcePwa = 4,
}

export enum DayOfWeek {
  Monday = 1,
  Tuesday = 2,
  Wednesday = 3,
  Thursday = 4,
  Friday = 5,
  Saturday = 6,
  Sunday = 0,
}

export enum EmailRequestMode {
  DoNotRequest = 0,
  Request = 1,
  Require = 2,
}

/* interface TimeSpan {
  Hours: number;
  Minutes: number;
  Seconds: number;
  Milliseconds: number;
  Ticks: number;
  Days: number;
  TotalDays: number;
  TotalHours: number;
  TotalMilliseconds: number;
  TotalMinutes: number;
  TotalSeconds: number;
} */

export interface BusinessHoursPeriod {
  BusinessHoursPeriodId: number;
  DayOfWeek: DayOfWeek;
  StartTime: string;
  Period: string;
  StartTimeEarly: string | null;
  PeriodEarly: string | null;
}

export enum MenuEntityAvailabilityMode {
  DisplayAlways = 0,
  DisplayBasedOnTimes = 1,
  DisplayAlwaysStartCollapsed = 2,
  DisplayAlwaysStartCollapsedBasedOnTimes = 3,
}

export enum MenuSectionBehaviour {
  ExpandSingle = 0,
  ExpandMultiple = 1,
}

export interface MenuSectionAvailability {
  MenuSectionId: number;
  MenuSection: string | null;
  AvailableTimes: BusinessHoursPeriod[] | null;
  AvailabilityMode: MenuEntityAvailabilityMode;
}

export enum CellLayoutType {
  Small = 0,
  Medium = 1,
  Large = 2,
}

export enum ContactFormType {
  None = 0,
  Email = 1,
  Intercom = 2,
  WebView = 3,
}

export enum AddressLayoutType {
  BuildingFirst = 0,
  AddressFirst = 1,
  FullStreetAddressFirst = 2,
}

export enum ComplianceType {
  Default = 0,
  GdprCompliance = 1,
}

export interface RestaurantSummaryCommon extends Opening {
  AcceptPreOrders: boolean;
  CuisineTypes: CuisineType[];
  Currency: Currency;
  DistanceKm: number;
  GeneralRating?: number;
  GeneralRatingCount: number;
  IanaTimeZone: string;
  IsoCurrency: string;
  IsOpen: boolean;
  MenuId: number;
  MenuUrl: string;
  MenuVersionNumber: number;
  PhysicalRestaurantAddress: string;
  PhysicalRestaurantId: number;
  PhysicalRestaurantName: string;
  StripePublicKey: string;
  TableServiceCatagory: TableServiceCategory;
  TimeZoneInfoId: string;
  UserRating?: any;
  UserRatingCount: number;
  VersionGuid: string;
  VirtualRestaurantId: number;
}

export interface CardFees {
  PercentFeeCardOrdersChargedToCustomerIncludingVat: number;
  FixedFeeCardOrdersChargedToCustomerIncludingVat: number;
  LocationServicePercentFeeCardOrdersChargedToCustomerIncludingVat: number;
  LocationServiceFixedFeeCardOrdersChargedToCustomerIncludingVat: number;
}

export interface CashFees {
  PercentFeeCashOrdersChargedToCustomerIncludingVat: number;
  FixedFeeCashOrdersChargedToCustomerIncludingVat: number;
  LocationServicePercentFeeCashOrdersChargedToCustomerIncludingVat: number;
  LocationServiceFixedFeeCashOrdersChargedToCustomerIncludingVat: number;
}

export interface Opening {
  IsOpen: boolean;
  ClosingTimeStamp?: Date;
  OpenTimeHour: number;
  OpenTimeMinute: number;
  OpenTimeMessage?: string;
  SecondsUntilRestaurantOpens?: number;
  SecondsUntilRestaurantCloses?: number;
}

export interface PreOrderTime {
  Key: string;
  Value: string;
  IsFull?: boolean;
}

export interface TipRate {
  Percentage: number;
  IsDefault: boolean;
}

export interface TipsConfig {
  AllowCustomTip: boolean;
  AllowEmojis: boolean;
  AllowRoundUp: boolean;
  Items: TipRate[];
}

export interface RestaurantDetail extends CardFees, CashFees, Opening {
  AcceptCardOrders: boolean;
  AcceptCashOrders: boolean;
  AcceptPreOrders: boolean;
  AcceptRestaurantVoucherOrders: boolean;
  AllowChefNotes: boolean;
  AppImageUrl: string;
  ChefNote?: string;
  Currency: Currency;
  DeliveryFeeTaxAmount: number;
  DisplayPhoneNumber: string;
  DisplayTax: boolean;
  FromAPI: boolean;
  MenuMessage?: string;
  MenuUrl: string;
  MenuVersionNumber: number;
  MinimumOrder: number;
  PhoneNumber: string;
  PhysicalRestaurantAddress: string;
  PhysicalRestaurantId: number;
  PreOrderOptions?: {
    PreOrderTimes: PreOrderTime[];
    RequireExplicitSelect: boolean;
  };
  ProcessingFeeTaxRate: number;
  RequireCustomerName: boolean;
  RestaurantName: string;
  ServiceChargeConfiguration: ServiceChargeConfig | null;
  TaxType: number;
  TimeZoneInfoId: string;
  TipsConfig: TipsConfig;
  TipsEnabled: boolean;
  VirtualRestaurantId: number;
}

export interface PickupLocationOption {
  PickupLocationOptionId: number;
  Value: string;
  DisplayOrder: number;
}

// Server PickupRestaurantDetailsResponse
export interface CollectionRestaurantDetail extends RestaurantDetail {
  AllowGuestCheckout: boolean;
  AllowPreOrdersAndTableService: boolean;
  DeliveryType: DeliveryType.Collection;
  PickupLocationOptions: PickupLocationOption[];
  PickupLocationTypes: PickupLocationType[];
  IsTableLockEnabled: boolean;
  TableServiceCatagory: TableServiceCategory;
  LocationAreas: LocationArea[];
}

// Server DeliveryRestaurantDetailsResponse
export interface DeliveryRestaurantDetail extends RestaurantDetail {
  UserOutsideDeliveryZone: boolean;
  DeliveryType: DeliveryType.Delivery;
  DeliveryFee: number;
}

// legacy - remove when Address Internationalisation is 100% released
export interface DisplayAddressFormat {
  oneLine: string;
  twoLine: string[];
}

// legacy - remove when Address Internationalisation is 100% released
export interface Address {
  Building?: string;
  Street?: string;
  Town?: string;
  PostCode?: string;
}

export interface DeliveryLocation extends Address {
  AddressFields?: any; // Make required when Address Internationalisation is 100% released
  AutoSelectThisLocation: boolean;
  Coordinates: Coordinates;
  CountryCode?: string; // Make required when Address Internationalisation is 100% released
  DeliveryInstructions?: string;
  DeliveryLocationId: number;
  displayAddress?: DisplayAddressFormat; // legacy - remove when Address Internationalisation is 100% released
  IsCustomAddress: boolean; // Can ignore
  IsDisplayed: boolean; // Hide false ones
  SingleLineDisplay?: string; // Make required when Address Internationalisation is 100% released
  TwoLinesDisplay?: string[]; // Make required when Address Internationalisation is 100% released
}

export const BlankDeliveryLocation = function (Geo: Coordinates) {
  return {
    AutoSelectThisLocation: false,
    Building: '',
    Coordinates: Geo,
    DeliveryInstructions: '',
    DeliveryLocationId: -1,
    IsCustomAddress: false,
    IsDisplayed: true,
    Street: '',
    Town: '',
  };
};

export interface AddressI18nPredefinedRule {
  Display: string;
  Value: string;
}

export interface AddressI18nFormRules {
  Predefined?: AddressI18nPredefinedRule[];
  Required?: {
    Message: string;
    Value: boolean;
  };
  MinLength?: {
    Message: string;
    Value: number;
  };
  MaxLength?: {
    Message: string;
    Value: number;
  };
  Pattern?: {
    Message: string;
    Value: RegExp;
  };
}

export interface AddressI18nTemplates {
  building: string;
  line1: string;
  line2: string;
  postcode: string;
  singleLine: string;
  street: string;
  town: string;
}

export interface AddressI18nDisplayFormat {
  OneLine: string;
  TwoLines: string[];
}

export interface AddressI18nFormData {
  Identifier: string;
  Label: string;
  Mapping: string;
  Placeholder?: string;
  Rules?: AddressI18nFormRules;
  Value?: string | number;
}

export interface AddressI18nFormScheme {
  CountryCode?: string;
  DisplayFormat?: AddressI18nDisplayFormat;
  FormData?: AddressI18nFormData[];
  Language?: string;
  Templates?: AddressI18nTemplates;
}

// Server name: DeliveryRestaurantSummaryDm
export interface DeliveryRestaurantSummary extends RestaurantSummaryCommon {
  DeliveryNote: string;
  HasConcessionStore?: boolean;
  IsFreeDelivery: boolean;
  Name: string;
  SkipToThisVirtualRestaurant: boolean;
  UserOutsideDeliveryZone: boolean;
}

// Server name: `PickupPhysicalRestaurantSummaryItem`
export interface CollectionRestaurantSummary extends RestaurantSummaryCommon {
  HasConcessionStore?: boolean;
  IncludePhysicalRestaurantIds: string;
  PhysicalRestaurantId: number;
  PickupLocationTypes: PickupLocationType[];
  SkipToThisPhysicalRestaurant: boolean;
  VirtualRestaurantName: string;
}

export interface PhoneNumberConfig {
  Prefix: string;
  AutocompletePhoneNumberLength: number;
}

export interface EnterPhoneNumberScreenTermsMessage {
  LinkText: string;
  LinkUrl: string;
  Text: string;
}

// AppDesignScheme to theme map
// isDark: theme.palette.mode === 'dark'
// Primary: theme.palette.background.default,
// Secondary: theme.palette.primary.main,
// Tertiary: theme.palette.background.default,
// Font: theme.typography.fontFamily
export interface AppDesignScheme {
  Primary: string;
  Secondary: string;
  Tertiary: string;
  Font?: string;
}

type PostCodeInputLabel = string | null;

export interface AppConfig {
  // Unused - DisplayBumpSideMenuItem
  AppDesignScheme: AppDesignScheme;
  AppNameId: string;
  AppStoreId: string;
  AppStoreUrl: string | null;
  BrandLogoUrl: string | null;
  BrandName: string | null;
  Category: number;
  ComplianceType: ComplianceType;
  ContactFormType: ContactFormType;
  CookiePolicyUrl?: string;
  CountryCode?: string;
  DataOffset?: number;
  DeliveryAddressLayout: number;
  DeliveryEnabled: boolean;
  DineInEnabled: boolean;
  DisplayConsentToSaveCard: boolean;
  DisplayCuisineTypeListAndRestaurantSearchBar: boolean;
  DisplayDeliveryRestaurantListScreen: boolean;
  DisplayFreeCokeSideMenuItem: boolean;
  DisplayPickupRestaurantListScreen: boolean;
  DisplayUserRestaurantRatings: boolean;
  EnterPhoneNumberScreenTermsMessage: EnterPhoneNumberScreenTermsMessage;
  FacebookTokens: string[];
  Features: string[];
  GoogleAnalyticsTokens: string[];
  GoogleMapsApiKeyWeb: string;
  Hostname: string;
  IntercomAppId: string;
  IntercomToken: string;
  IsEmbed: boolean;
  Languages: [Language];
  MapBoundryMaxLatitude: number;
  MapBoundryMaxLongitude: number;
  MapBoundryMinLatitude: number;
  MapBoundryMinLongitude: number;
  MapCenterLatitude: number;
  MapCenterLongitude: number;
  MixpanelToken: string;
  PhoneNumberConfig: PhoneNumberConfig;
  PickupEnabled: boolean;
  PlayStoreUrl: string | null;
  PostCodeInputLabel: PostCodeInputLabel;
  PrivacyPolicyUrl?: string;
  ShareMessageEmailMessage?: string;
  ShareMessageEmailSubject?: string;
  ShareMessageEmailUrl?: string;
  ShareMessageFacebookMessage?: string;
  ShareMessageFacebookUrl?: string;
  ShareMessageGenericMessage?: string;
  ShareMessageGenericUrl?: string;
  ShareMessageSmsMessage?: string;
  ShareMessageSmsUrl?: string;
  ShareMessageTwitterMessage?: string;
  ShareMessageTwitterUrl?: string;
  ShareMessageWhatsAppMessage?: string;
  ShareMessageWhatsAppUrl?: string;
  TermsAndConditionsUrl?: string;
  TipsEnabled: boolean;
  ViewportTopOffset?: number;
  WebOrderUiVersion?: string;
  WebToAppRedirect: WebToAppRedirect;
  WhiteLabelId: number;
}

export interface MenuItemOptionSetItem extends Common {
  MenuItemOptionSetItemId: number;
  NextMenuItemOptionSetId?: number;
  IsAvailable: boolean;
  TaxValue?: number;
}

export interface MenuItemOptionSet extends Common {
  MenuItemOptionSetId: number;
  IsMasterOptionSet: boolean;
  MinSelectCount: number;
  MaxSelectCount: number;
  MenuItemOptionSetItems: MenuItemOptionSetItem[];
}

export interface MenuItem extends Common {
  ActualPrice: number;
  Alcohol: boolean;
  CatalogItemId: number;
  ConcessionStoreId?: number;
  DailySpecialHours: any[];
  Description?: string;
  DisableVouchers: boolean;
  ExcludeFromVoucherDiscounting: boolean;
  ExternalImageUrl: string;
  ImageName: string;
  IsAvailable: boolean;
  IsDeleted: boolean;
  MenuId: number;
  MenuItemId: number;
  MenuItemMetadata: any[];
  MenuItemOptionSets: MenuItemOptionSet[];
  MenuSectionId: number;
  PriceCanIncrease: boolean;
  PublicId: string;
  SpicinessRating: number;
  Tags: any[];
  TaxRateId: number;
  TaxRateName: string;
  TaxValue: number;
}

export interface ReadonlyMenuResponse {
  MenuId: number;
  VersionNumber: number;
  MenuSections: any[];
  DisplaySectionLinks: boolean;
  MenuSectionBehaviour: Fd.Menu['MenuSectionBehaviour'];
  FromAPI: boolean;
  Locked: boolean;
}

export interface MenuReposnse {
  ConcessionStores: ConcessionStore[];
  Currency: Currency;
  DisplaySectionLinks: boolean;
  FromAPI: boolean;
  MenuId: number;
  MenuSectionBehaviour: MenuSectionBehaviour;
  MenuSections: any[];
  MenuUrl: string;
  MenuVersionNumber: number;
  VersionGuid: string;
  VirtualRestaurantId: number;
}

export interface Customer {
  ConsentRequest: ConsentRequestData;
  CustomerName: string | null;
  CustomerPhoneNumberLocalFormatString: string;
  DefaultTipPercentage: number;
  DisplayTipOption: boolean;
  EmailAddress: string | null;
  EmailRequestMode: EmailRequestMode;
  IsGuest: boolean;
  Language: string;
  PhoneNumber: string;
  RequireAgeConfirmationForAlcoholOrdersData: RequireAgeConfirmationForAlcoholOrdersData;
  SmsPromotionsEnabled: boolean;
  UserId: number;
}

export interface RequireAgeConfirmationForAlcoholOrdersData {
  ConfirmButtonText: string;
  DenyButtonText: string;
  MessageText: string;
  DisplayConfirmationPrompt: boolean;
}

export interface ConsentRequestData {
  RequestConsent: boolean;
  MessageText: string | null;
  ConfirmButtonText: string | null;
  DenyButtonText: string | null;
  SmallPrintText: string | null;
  SmallPrintUrl: string | null;
}

export interface PaymentAccount {
  UserId: number;
  PaymentAccountId: number;
  CanRemoveFromCustomerView: boolean;
  Bin?: string;
  IsDefaultPaymentMethod: boolean;
  PaymentAccountType:
    | 'CardPaymentAccount'
    | 'CashPaymentAccount'
    | 'BrainTreePayPalPaymentAccount'
    | 'StripePayButtonPaymentAccount'
    | 'PayGreenRestaurantVoucher'
    | GatewayPaymentAccount;
  CustomerId: string;
  TokenizationKey: string;
  PublicApiKey: string;
  Description: string;
  SupportsSubmitOrderTips: boolean;
}

export interface PreviousOrderItemOptionDm {
  MenuItemOptionId: number;
  IsMasterOptionSetItem: boolean;
  Name: string;
  Price: number;
  MenuItemOptionDisplayOrder: number;
  MenuItemOptionSetDisplayOrder: number;
}

export interface PreviousOrderItemVms {
  VirtualRestaurantId: number;
  MenuSectionName: string;
  MenuSectionId: number;
  Name: string;
  Description: string;
  Price: number;
  PriceIncludingOptionSetItems: number;
  OrderItemId: number;
  MenuItemId: number;
  IsAvailable: boolean;
  PreviousOrderItemOptionVms: PreviousOrderItemOptionDm[];
  MenuSectionDisplayOrder: number;
  MenuItemDisplayOrder: number;
}

export interface CustomerOrderView {
  OrderId: number;
  VirtualRestaurantId: number;
  PhysicalRestaurantId?: number;
  DeliveryType: DeliveryType;
  RestaurantName: string;
  TipAmount: number;
  DeliveryAmount: number;
  FoodAmount: number;
  CardFee: number;
  TotalAmount: number;
  TsEditTipEndTimeUtc?: string; // time object
  TsUpdate: string; // time object
  UserRating?: number;
  CuisineType: CuisineType;
  PaymentAccountDescription: string; // Visa **** 2371. or Apple Pay. or Cash
  PreviousOrderItemVms: PreviousOrderItemVms[];
  IsHiddenFromRecentOrdersList: boolean;
  Currency: Currency;
  TsOrderPlaced: string; // date object
  DeliveryLocationAddressString: string; // eg. 20 Waterloo Road
  DeliveryLocationId?: number;
  DeliveryInstructions: string;
  CoordinatesDm: Coordinates;
  OrderState: OrderState;
}

export interface DeliveryLocationResponse {
  SkipToDeliveryRestaurantsScreen: boolean;
  SkipToPickupRestaurantsScreen: boolean;
  DeliveryLocations: DeliveryLocation[];
}

export interface ApplyVoucherProps {
  orderId: number;
  voucherCode: string;
  paymentAccountId?: number;
}

export interface SubmitOrderBase {
  chefNote: string;
  excludeServiceCharge?: boolean;
  orderId: number;
  refererUrl?: string;
}
export interface SubmitOrderProps extends SubmitOrderBase {
  paymentAccountId: number;
}
export interface SubmitPayPalOrderProps extends SubmitOrderBase {
  clientToken: string;
  deviceData: string;
}
export interface SubmitPayStripeOrderProps extends SubmitOrderBase {
  stripePaymentMethodId: string;
  physicalRestaurantId?: number;
}

export interface PreviousOrderItemDm {
  MenuItemPublicId: string;
  MenuSectionPublicId: string;

  VirtualRestaurantId: number;
  MenuSectionName: string;
  MenuSectionId: number;
  Name: string;
  Description: string;
  Price: number;
  PriceIncludingOptionSetItems: number;
  OrderItemId: number;
  MenuItemId: number;
  IsAvailable: boolean;
  PreviousOrderItemOptionVms: PreviousOrderItemOptionDm[];
  MenuSectionDisplayOrder: number;
  MenuItemDisplayOrder: number;

  IsVoucher: boolean;

  Metadata: { [key: string]: string };
}

export enum RejectionReason {
  TooBusy = 0,
  FoodUnavailable = 1,
  UnableToDeliver = 2,
  UnknownAddress = 3,
  UnknownReason = 4,
  TooSoon = 5,
  TimeUnavailable = 6,
  DontDeliverToArea = 7,
  RestaurantUncontactable = 8,
}

export enum Warning {
  InsideFraudZone = 1,
  UnusuallyHighValueOrder = 2,
}
export enum DeliveryTrackingStatus {
  /** Not assigned to driver */
  Unassigned = 10,
  /** Assigned but not accepted */
  Unaccepted = 15,
  /** Accepted by driver */
  Accepted = 20,
  /** Carrying */
  Carrying = 30,
  /** On the way */
  OnTheWay = 60,
  /** Arrived at location */
  ArrivedAtLocation = 70,
  /** Delivered */
  Delivered = 80,
  /** Cannot deliver */
  CannotDeliver = 90,
}
export enum LocationCertainty {
  Hardcoded = 1,
  AppConfig = 2,
  GeoIp = 3,
  Settings = 4,
  GeoLocation = 5,
  User = 6,
}

export interface PreviousOrderDm {
  OrderId: number;
  LocalOrderId: string;
  VirtualRestaurantId: number;
  WhiteLabelId: number;
  UserId: number;
  PhysicalRestaurantId: number | null;
  DeliveryType: DeliveryType;
  PickupLocationType: PickupLocationType;
  PickupLocationId: number | null;
  PickupLocationOptionValue: string;
  TableServiceCatagory: TableServiceCategory;
  RestaurantName: string;
  TipAmount: number;
  DeliveryAmount: number;
  FoodAmount: number;
  CardFee: number;
  ProcessingFee: number;
  TotalAmount: number; // TotalCustomerAmount
  TsEditTipEndTimeUtc: Date | string | null;
  TsUpdate: Date | string;

  // response for 3d secure failure
  PaymentProcessingResponse: string | null;

  UserRating: number | null;

  CuisineType: CuisineType;
  /** Visa **** 2371. or Apple Pay. or Cash */
  PaymentAccountDescription: string;

  IsExternalPaymentAccountType: boolean;

  PreviousOrderItemVms: PreviousOrderItemDm[];

  IsHiddenFromRecentOrdersList: boolean;

  Currency: Currency;
  IsoCurrency: string;

  TsOrderPlaced: Date | string | null;

  DeliveryLocationAddressString: string; // eg. 20 Waterloo Road

  DeliveryLocationId: number | null;

  DeliveryLocation: DeliveryLocation;

  DeliveryInstructions: string;
  PhysicalRestaurantCoordinates: Coordinates;
  Coordinates: Coordinates;
  UserLocation: Coordinates;

  MenuId: number;

  RejectionReason: RejectionReason | null;
  KioskCustomerName: string;

  // #region RestaurantOrders

  TotalRestaurantAmount: number;

  PaymentAccountType: PaymentAccountType;

  OrderState: OrderState;

  CustomerName: string;

  EmailAddress: string;

  IsPreOrder: boolean;

  TsRequestedFor: Date | string | null;

  AmendmentForOrderId: number | null;

  HasAmendmentOrder: boolean;

  PreviousOrdersAtPhysicalRestaurantCount: number;
  PhysicalRestaurantTimeZoneInfoId: string;
  ChefNote: string;
  NetTipAmount: number;
  // Voucher: Voucher;
  VoucherAmount: number | null; // Eg. €5 off voucher will have an amount of -5.00
  // AppType: AppType;
  // PrintoutLayoutType: PrintoutLayoutType;
  UserMetadata: { [key: string]: string };
  PhysicalRestaurantMetadata: { [key: string]: string };

  Warnings: Warning;

  FoodTaxAmount: number;
  TotalTax: number;
  TipTaxAmount: number;
  DeliveryTaxAmount: number;
  // #endregion

  RefundedAmount: number | null;
  WasRedirectRequired: boolean;

  /** Delivery tracking status */
  DeliveryTrackingStatus: DeliveryTrackingStatus | null;

  /** Assigned driver identifier */
  DriverId: number | null;

  /** Time that the order was accepted for by store */
  AcceptedFor: Date | string | null;

  /** Id of user who rejected order */
  RejectedByUserId: number | null;

  RedirectUrl: string;

  /** Receipt code for external receipt* */
  ReceiptCode: string | null;
}

export interface Location {
  DisplayOrder: number;
  ExternalLocationId: string;
  LocationId: number;
  LocationName: string;
  IsDeleted: boolean;
}

export interface LocationArea {
  LocationAreaId: number;
  LocationAreaName: string;
  Locations: Location[];
  IsDeleted: boolean;
  StoreId: number;
}

// ! Deprecated - use Result from src/core/types/data.types.ts
// Common reposonse JSON
export interface Result<T> {
  Code: number;
  Data: T;
  DeveloperMessage: string;
  StackTrace: string;
  Success: boolean;
  UserMessage: string;
}
